import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Play, Pause } from '@videoblocks/react-icons';

import { VideoSelection } from '../utils/videoUtils';

function formatTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
    .toString()
    .padStart(2, '0')}`;
}

interface VideoPlayerProps {
  src: string;
  selection?: VideoSelection;
  isPlaying: boolean;
  onPlayPause: () => void;
  onTimeUpdate: (currentTime: number) => void;
  className?: string;
  currentTime?: number; // External time control for seeking
}

export default function VideoPlayer({
  src,
  selection,
  isPlaying,
  onPlayPause,
  onTimeUpdate,
  className = '',
  currentTime,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      setVideoDuration(video.duration);
      setIsVideoReady(true);
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      onTimeUpdate(video.currentTime);

      // If we have a selection and we've reached the end, loop
      if (selection && video.currentTime >= selection.endTime) {
        video.currentTime = selection.startTime;
        onTimeUpdate(video.currentTime);
        video.play();
      }
    }
  }, [onTimeUpdate, selection]);

  const handlePlay = useCallback(() => {
    const video = videoRef.current;
    if (video && selection) {
      // If we're at or past the end of selection, restart from beginning
      if (
        video.currentTime >= selection.endTime ||
        video.currentTime < selection.startTime
      ) {
        video.currentTime = selection.startTime;
      }
    }
  }, [selection]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideoReady) return;

    if (isPlaying) {
      video.play();
    } else {
      video.pause();
    }
  }, [isPlaying, isVideoReady]);

  useEffect(() => {
    const video = videoRef.current;
    if (video && selection && isVideoReady) {
      // If current time is outside selection, move to start of selection
      if (
        video.currentTime < selection.startTime ||
        video.currentTime > selection.endTime
      ) {
        video.currentTime = selection.startTime;
      }
    }
  }, [selection, isVideoReady]);

  // Handle external currentTime updates (e.g., from dragging selection indicator)
  useEffect(() => {
    const video = videoRef.current;
    if (video && isVideoReady && currentTime !== undefined) {
      // Only seek if the external time is different from current video time
      if (Math.abs(video.currentTime - currentTime) > 0.1) {
        video.currentTime = currentTime;
      }
    }
  }, [currentTime, isVideoReady]);

  // Keyboard controls
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const video = videoRef.current;
      if (!video || !isVideoReady) return;

      switch (e.key) {
        case ' ':
        case 'Enter':
          e.preventDefault();
          onPlayPause();
          break;
      }
    },
    [onPlayPause, selection, isVideoReady, videoDuration]
  );

  // Handle container click for play/pause
  const handleContainerClick = () => {
    onPlayPause();
  };

  return (
    <div
      className={`relative ${className} focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 rounded-lg`}
      onClick={handleContainerClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label="Video player - press space to play/pause"
    >
      <video
        ref={videoRef}
        src={src}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onPlay={handlePlay}
        className="w-full h-full object-contain rounded-lg"
        preload="metadata"
        muted
      />

      {/* Top gradient overlay */}
      <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-black to-transparent rounded-t-lg pointer-events-none" />

      {/* Bottom gradient overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black to-transparent rounded-b-lg pointer-events-none" />

      {isVideoReady && (
        <div className="absolute bottom-3 left-3 flex items-center gap-2 pointer-events-auto">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPlayPause();
            }}
            className="flex items-center justify-center hover:bg-white hover:bg-opacity-10 rounded p-1 transition-colors"
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
          >
            {isPlaying ? (
              <Pause className="w-4 h-4 fill-white" />
            ) : (
              <Play className="w-4 h-4 fill-white" />
            )}
          </button>
          <div className="text-white text-sm font-sans">
            {formatTime(currentTime || 0)} / {formatTime(videoDuration)}
          </div>
        </div>
      )}
    </div>
  );
}
